import { Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { ModalDirective } from 'angular-bootstrap-md';
import { Transaction } from 'src/app/shared/models/transactions/transaction';
import { TransactionService } from 'src/app/services/transaction.service';
import { TransactionDeals } from 'src/app/shared/models/transaction-deals/transaction-deal';
import { TransactionStatus } from 'src/app/shared/models/transactions/transaction-status';
import { TransactionDealService } from 'src/app/services/transaction-deal.service';
import { NgForm } from '@angular/forms';
import { TransactionStatusUtil } from 'src/app/utils/transaction-status-util';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-trade-edit',
  templateUrl: './trade-edit.component.html',
  styleUrls: ['./trade-edit.component.scss'],
})
export class TradeEditComponent implements OnInit, OnChanges {
  @ViewChild('tradeEditModal', { static: true }) tradeEditModal: ModalDirective;

  @Input() showModal: boolean = false;
  @Input() transaction: Transaction;
  @Input() deal: TransactionDeals;
  @Input() status: TransactionStatus;
  @Input() fullStatusList: TransactionStatus[];

  @Output() showModalChange = new EventEmitter<boolean>();
  @Output() onModelChange = new EventEmitter<Transaction>();
  @Output() onError = new EventEmitter<Transaction>();

  statusId: number;
  originalDealRate: number;
  editEnabled: boolean = false;
  editDealRateEnabled: boolean = false;
  busy: boolean = false;
  statusList: TransactionStatus[];
  transactionId: number;
  apiInfoMessageArray: string[] = [];

  saveBtnVisible: boolean = false;
  reprocessBtnVisible: boolean = false;
  dealRateVisible: boolean = false;
  apiAlreadySaved: boolean = false;
  confirmCFSStatusBtnVisible: boolean = false;
  manualStatusCheckMessage = 'Note: Click "Get Deal Status Now" below';
  busyText = '';
  containsError = false;
  canReprocess: boolean = false;
  showOverrideBox: boolean = false;
  isPeriodElapsed: boolean = false;
  futureTimeAsDate: Date = null;
  override: boolean = false;
  reprocessAcknowledgement: boolean = false;
  isDealNotFound: boolean = false;

  constructor(
    private transactionService: TransactionService,
    private dealService: TransactionDealService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.originalDealRate = this.deal?.dealRate;
  }

  getApiErrorDetail() {
    this.apiInfoMessageArray = [];
    if (this.deal?.lastMessage) {
      const message = this.deal.lastMessage;
      const messageParts = message.split(/{(.+)/);
      if (messageParts.length > 0) {
        if (messageParts.length === 1) {
          this.apiInfoMessageArray.push(messageParts[0]);
        } else {
          this.apiInfoMessageArray.push(messageParts[0]);
          this.apiInfoMessageArray.push(messageParts[1]);
          this.containsError = true;
        }
      }
    }
  }

  ngOnChanges(): void {
    this.transactionId = this.transaction.id;
    this.statusId = this.transaction.transactionStatusId;
    this.toggleUIElements();
    //clear list
    this.statusList = [];
    if (this.editEnabled) {
      this.buildStatusList();
    } else {
      this.addCurrentStatus();
    }
    this.showHideModal(this.showModal);
  }

  private toggleUIElements() {
    this.enableSelect();
    this.enableDealRate();
    this.enableSave();
    this.enableConfirmCFSStatus();
    this.enableReprocessButton();
    this.actOnLastUpdatedDate();
    this.getApiErrorDetail();
  }

  private enableSelect() {
    if (TransactionStatus.canChangeStatus(this.statusId)) {
      this.editEnabled = true;
    } else {
      this.editEnabled = false;
    }
  }

  public enableSave() {
    if (this.apiAlreadySaved) {
      this.saveBtnVisible = false;
      this.apiAlreadySaved = false;
      return;
    }

    this.saveBtnVisible = TransactionStatus.canShowSaveButton.includes(this.statusId);
  }

  private enableConfirmCFSStatus() {
    this.confirmCFSStatusBtnVisible = false;
    const retryProblemLimit = 10;

    if (TransactionStatus.confirmCFSStatusBtnVisibleList.includes(this.statusId) == false) {
      return;
    }

    const confirmDealNotFound = 235;
    if (this.statusId == confirmDealNotFound) {
      this.confirmCFSStatusBtnVisible = true;
      return;
    }

    if (this.deal?.retryCount < retryProblemLimit) {
      return;
    }

    this.confirmCFSStatusBtnVisible = true;
  }

  onReprocessAcknowledgementChange() {
    if (this.reprocessAcknowledgement) {
      this.reprocessBtnVisible = true;
    } else {
      this.reprocessBtnVisible = false;
    }
  }

  public enableReprocessButton() {
    this.reprocessBtnVisible = false;
    this.showOverrideBox = false;
    this.override = false;
    this.reprocessAcknowledgement = false;

    if (TransactionStatus.canReprocessTrade.includes(this.statusId) == false) {
      return;
    }
    const confirmOnCFS = 227;
    const confirmDealNotFound = 235;
    if (this.statusId == confirmOnCFS) {
      this.showOverrideBox = true;
      this.reprocessBtnVisible = this.canReprocess;
      return;
    }
    if (this.statusId == confirmDealNotFound) {
      this.showOverrideBox = true;
      this.isDealNotFound = true;
      this.reprocessBtnVisible = this.canReprocess;
      return;
    }

    this.reprocessBtnVisible = true;
  }

  private enableDealRate() {
    this.dealRateVisible = false;
    this.editDealRateEnabled = false;
    if (TransactionStatus.canEditDealRateStatusList.includes(this.statusId)) {
      this.dealRateVisible = true;
      this.editDealRateEnabled = true;
    }
  }

  private buildStatusList() {
    //add current status
    this.addCurrentStatus();
    //add changeable status
    this.addSelectableStatus();
  }

  private fillSelectableStatusList() {
    if (TransactionStatus.canReprocessTrade.includes(this.statusId)) {
      const bookManualStatus = TransactionStatusUtil.FindById(this.fullStatusList, 210);
      this.statusList.push(bookManualStatus);
    }
    if (TransactionStatus.canReprebookTrade.includes(this.statusId)) {
      const rePrebookStatus = TransactionStatusUtil.FindById(this.fullStatusList, 20);
      this.statusList.push(rePrebookStatus);
    }
    if (TransactionStatus.canRetryLinkPrebookedDooyaTrade.includes(this.statusId)) {
      const retryLinkPrebookedDooyaTrade = TransactionStatusUtil.FindById(this.fullStatusList, 60);
      this.statusList.push(retryLinkPrebookedDooyaTrade);
    }
    if (TransactionStatus.canDeleteTrade.includes(this.statusId)) {
      const deleteStatus = TransactionStatusUtil.FindById(this.fullStatusList, 999);
      this.statusList.push(deleteStatus);
    } else {
      const dealReleasedStatus = TransactionStatusUtil.FindById(this.fullStatusList, 240);
      this.statusList.push(dealReleasedStatus);
    }
  }

  private addSelectableStatus() {
    const statusSelectDisabled = this.statusList.find((x) => {
      return TransactionStatus.isAuthorisedDone(x.id);
    });

    if (!statusSelectDisabled) {
      this.fillSelectableStatusList();
    } else {
      this.editEnabled = false;
    }
  }

  private addCurrentStatus(): void {
    if (this.status == null) {
      return;
    }

    this.statusList.push(this.status);
  }

  selectionChanged() {
    // reset this so that save button shows
    // changing the selection box implies that there's something needing saving
    this.apiAlreadySaved = false;
    this.enableDealRate();
    this.enableSave();
    this.enableReprocessButton();
  }

  saveClicked(form: NgForm | undefined) {
    // stop here if form is invalid
    if (form?.invalid) {
      form.form.markAllAsTouched();
      return;
    }
    this.busy = true;
    this.busyText = 'Saving trade...';
    this.transaction.transactionDeals = [];
    this.transaction.originalTransactionStatusId = this.transaction.transactionStatusId;
    this.transaction.transactionStatusId = this.statusId;
    this.transaction.transactionDeals.push(this.deal);

    if (this.originalDealRate != this.deal?.dealRate) {
      this.transaction.dealRateChangedText = 'please refresh';
    }

    this.transactionService.UpdateTransaction(this.transaction).subscribe({
      next: (updatedTransaction: Transaction) => {
        this.onModelChange.emit(updatedTransaction);
        this.busy = false;
        this.showHideModal(false);
      },
      error: (error) => {
        this.busy = false;
        this.transaction.error = error;
        this.onError.emit(this.transaction);
        this.showHideModal(false);
      },
    });
  }

  public reprocessTrade() {
    this.busy = true;
    this.busyText = 'Reprocessing trade...';
    this.transaction.reprocessTrade = true;
    this.transaction.originalTransactionStatusId = this.transaction.transactionStatusId;
    this.transactionService.UpdateTransaction(this.transaction).subscribe({
      next: (updatedTransaction: Transaction) => {
        this.onModelChange.emit(updatedTransaction);
        this.busy = false;
        this.showHideModal(false);
      },
      error: (error) => {
        this.transaction.error = error;
        this.onError.emit(this.transaction);
        this.busy = false;
        this.showHideModal(false);
      },
    });
  }

  public dealLastUpdated(): string {
    if (this.deal?.lastUpdated == null || this.deal?.created == null) {
      return '(Awaiting Book Now)';
    }

    let updatedDate = new Date(this.deal.lastUpdated);
    let formattedDate = this.datePipe.transform(updatedDate, 'd MMM - H:mm ');
    return `${formattedDate} (${this.getMinutesAgo(updatedDate)} mins ago)`;
  }

  public getMinutesAgo(date: Date): number {
    const now = new Date();
    const diffInMilliseconds = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
    return diffInMinutes;
  }

  public actOnLastUpdatedDate() {
    const since = new Date(this.deal?.lastUpdated);
    const minutesToWait = 20;
    this.getFutureTimeInMinutesFromPeriod(since, minutesToWait);
    this.hasPeriodElapsed(since, minutesToWait);
  }

  private getFutureTimeInMinutesFromPeriod(since: Date, minutes: number) {
    const periodMinutesInMilliseconds = minutes * 60 * 1000; // provided minutes in milliseconds
    const dateAsTime = new Date(since.getTime() + periodMinutesInMilliseconds);
    this.futureTimeAsDate = dateAsTime;
  }

  private hasPeriodElapsed(since: Date, minutes: number) {
    const now = new Date();
    const diffInMilliseconds = now.getTime() - since.getTime();
    const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));

    this.isPeriodElapsed = diffInMinutes >= minutes;
  }

  checkUpdateStatus() {
    if (this.updateSuccess === null) {
      return '';
    }

    if (this.updateSuccess === true) {
      return 'update-success';
    }

    return 'update-fail';
  }
  updateSuccess: boolean | null = null;

  checkCFSStatus() {
    this.busy = true;
    this.busyText = 'Confirming CFS Status...';
    this.transaction.transactionDeals.push(this.deal);
    this.transaction.originalTransactionStatusId = this.transaction.transactionStatusId;
    this.dealService.UpdateDealAndTransactionStatus(this.transaction).subscribe({
      next: (updatedTransaction: Transaction) => {
        this.buildStatusList();
        var newStatusId = updatedTransaction.transactionStatusId;
        this.status = TransactionStatusUtil.FindById(this.fullStatusList, newStatusId);
        this.apiAlreadySaved = true;
        this.onModelChange.emit(updatedTransaction);
        this.updateSuccess = true;
        setTimeout(() => {
          this.updateSuccess = null;
        }, 3000);

        this.busy = false;
      },
      error: (error) => {
        this.transaction.error = error;
        this.onError.emit(this.transaction);

        this.updateSuccess = false;
        setTimeout(() => {
          this.updateSuccess = null;
        }, 3000);

        this.busy = false;
        this.showHideModal(false);
      },
    });
  }

  showHideModal(toggle: boolean) {
    if (toggle != null) {
      this.showModal = toggle;
    }
    if (this.showModal) {
      this.tradeEditModal.show();
    } else if (!this.showModal) {
      this.tradeEditModal.hide();
    }
    this.override = false;
    this.reprocessAcknowledgement = false;
    this.showModalChange.emit(this.showModal);
  }

  showRefreshDooyaButton(): boolean {
    return TransactionStatus.canShowRefreshDooyaButton.includes(this.statusId);
  }

  refreshDooyaStatus(): void {
    this.busy = true;
    this.busyText = 'Refreshing trade from Dooya ...';
    this.transaction.originalTransactionStatusId = this.transaction.transactionStatusId;
    this.transactionService.RefreshDooyaStatus(this.transaction.id).subscribe({
      next: (updatedTransaction: Transaction) => {
        this.onModelChange.emit(updatedTransaction);
        this.busy = false;
        this.showHideModal(false);
        this.transaction.recentlyUpdated = true;
      },
      error: (error) => {
        this.transaction.error = error;
        this.onError.emit(this.transaction);
        this.busy = false;
        this.showHideModal(false);
      },
    });
  }

  canRetryPrebooking(trade: Transaction): boolean {
    return TransactionStatus.canReprebookTrade.includes(this.statusId);
  }

  canLinkPrebookedTrade(trade: Transaction): boolean {
    return TransactionStatus.canRetryLinkPrebookedDooyaTrade.includes(this.statusId);
  }

  canDelete(trade: Transaction): boolean {
    return TransactionStatus.canDeleteTrade.includes(this.statusId);
  }

  retryPrebooking(): void {
    this.statusId = 20;
    this.saveClicked(null);
  }

  linkPrebookedTrade(): void {
    this.statusId = 60;
    this.saveClicked(null);
  }

  deleteTrade(): void {
    this.statusId = 999;
    this.saveClicked(null);
  }

  overriddenDealTypeChanged(): void {
    this.apiAlreadySaved = false;
    if (this.saveBtnVisible) {
      return;
    }

    if (this.canRetryPrebooking(this.transaction) || this.canLinkPrebookedTrade(this.transaction) || this.canDelete(this.transaction)) {
      return;
    }

    this.saveBtnVisible = !this.reprocessBtnVisible;
  }

  canEditDealType(): boolean {
    return TransactionStatus.canEditDealType.includes(this.statusId);
  }
}
